import { useMemo } from 'react';
import { APP_NAV } from '../context/AppNavigationContext';
import { FILTER_PAGE_IDS, usePageSettings } from '../context/PageDashboardContext';
import { useTenantView } from '../context/TenantViewContext';
import { useAuth } from '../context/AuthContext';
import { useTopEvents } from './useTopEvents';
import { useRevenueSummary } from './useRevenueSummary';
import { useConvertedCurrencyData } from './useConvertedCurrencyData';
import { exportPageDashboardData } from '../lib/exportCsv';
import { exportDashboardPdf } from '../lib/exportPdf';
import { formatMoney, formatNumber, formatPercent } from '../lib/formatMoney';
import { hasExchangeRate } from '../lib/convertDashboardData';
import { useCurrency } from '../context/CurrencyContext';
import {
  collectAvailableVenues,
  isValidEventName,
  productTypeLabel,
  TIME_PERIOD_LABELS,
} from '../pages/dashboard/dashboardUtils';

const PAGE_REPORT_TITLES = {
  [APP_NAV.OVERVIEW]: 'Overview Report',
  [APP_NAV.TRENDS]: 'Trends Report',
  [APP_NAV.PRODUCTS]: 'Products Report',
  [APP_NAV.EVENTS]: 'Events Report',
  [APP_NAV.VENUES]: 'Venues Report',
};

const PAGE_REPORT_VARIANTS = {
  [APP_NAV.OVERVIEW]: 'overview',
  [APP_NAV.TRENDS]: 'trends',
  [APP_NAV.PRODUCTS]: 'products',
  [APP_NAV.EVENTS]: 'events',
  [APP_NAV.VENUES]: 'venues',
};

const PAGE_EXPORT_PREFIXES = {
  [APP_NAV.OVERVIEW]: 'venue-intelligence-overview',
  [APP_NAV.TRENDS]: 'venue-intelligence-trends',
  [APP_NAV.PRODUCTS]: 'venue-intelligence-products',
  [APP_NAV.EVENTS]: 'venue-intelligence-events',
  [APP_NAV.VENUES]: 'venue-intelligence-venues',
};

export function useDashboardData(pageId, { enabled = true } = {}) {
  const { isSuperAdmin } = useAuth();
  const { viewingTenant } = useTenantView();
  const isFilterPage = FILTER_PAGE_IDS.includes(pageId);
  const settingsPageId = isFilterPage ? pageId : APP_NAV.OVERVIEW;

  const {
    committedFilters,
    displayCurrency,
    currencyConverting,
    setCommittedFilters,
    setDisplayCurrency,
    setCurrencyConverting,
    sourceCurrency,
  } = usePageSettings(settingsPageId);

  const { rates, ratesLoading, ratesError } = useCurrency();

  const revenueQueryString = useMemo(() => {
    const params = new URLSearchParams(committedFilters.queryString || '');
    if (isSuperAdmin && viewingTenant?.id) {
      params.set('accountId', String(viewingTenant.id));
    }
    return params.toString();
  }, [committedFilters.queryString, isSuperAdmin, viewingTenant?.id]);

  const { data: rawData, dataSource, loading, error } = useRevenueSummary(
    revenueQueryString,
    { enabled: enabled && isFilterPage }
  );

  const topEventFilters = useMemo(
    () => ({
      start: committedFilters.start,
      end: committedFilters.end,
      venueId: committedFilters.venue !== 'all' ? Number(committedFilters.venue) || null : null,
      eventId: committedFilters.event !== 'all' ? Number(committedFilters.event) || null : null,
      productType: committedFilters.productType !== 'all' ? committedFilters.productType : null,
      accountId: isSuperAdmin && viewingTenant?.id ? viewingTenant.id : null,
    }),
    [committedFilters, isSuperAdmin, viewingTenant?.id]
  );

  const needsTopEvents = pageId === APP_NAV.OVERVIEW || pageId === APP_NAV.EVENTS;

  const {
    isLoading: isTopEventsLoading,
    error: topEventsError,
    rows: rawTopEventRows,
    comparison: topEventsComparison,
  } = useTopEvents({
    filters: topEventFilters,
    includeComparison: true,
    enabled: enabled && isFilterPage && needsTopEvents,
    revenueSummary: rawData,
    revenueSummaryLoading: loading,
  });

  const { data, rows: topEventRows, waitingForRates } = useConvertedCurrencyData({
    rawData,
    rawRows: rawTopEventRows,
    displayCurrency,
    rates,
    ratesLoading,
    sourceCurrency,
    setCurrencyConverting,
  });

  const isFirstLoad = !data && loading;
  const isRefreshing = !!data && loading;
  const needsConversion = displayCurrency !== sourceCurrency;
  const currencyLoading = currencyConverting || waitingForRates;
  const currencyUnavailable = needsConversion && !currencyLoading && Boolean(ratesError);
  const panelLoading = isFirstLoad || isRefreshing;

  const summary = data?.summary || null;
  const currencyCode = currencyLoading && needsConversion
    ? (rawData?.summary?.currencyCode || sourceCurrency)
    : hasExchangeRate(rates, displayCurrency, sourceCurrency)
      ? displayCurrency
      : (rawData?.summary?.currencyCode || sourceCurrency);
  const revenueComparison = data?.periodComparison?.overall || null;
  const totalRevenueTrend = revenueComparison
    ? Number(revenueComparison.absoluteChange || 0) >= 0 ? 'up' : 'down'
    : 'neutral';
  const totalRevenueChange = revenueComparison
    ? formatPercent(revenueComparison.percentChange)
    : '—';

  const availableEvents = useMemo(() => {
    const seen = new Set();
    const events = [];

    const topEvents = data?.topEvents || [];
    for (const event of topEvents) {
      if (event.eventId !== null && event.eventId !== undefined && isValidEventName(event.eventName)) {
        const key = String(event.eventId);
        if (!seen.has(key)) {
          seen.add(key);
          events.push({
            eventId: event.eventId,
            eventName: event.eventName,
            venueId: event.venueId || null,
          });
        }
      }
    }

    const allEvents = data?.allEvents || data?.events || [];
    for (const event of allEvents) {
      if (event.eventId !== null && event.eventId !== undefined && isValidEventName(event.eventName)) {
        const key = String(event.eventId);
        if (!seen.has(key)) {
          seen.add(key);
          events.push({
            eventId: event.eventId,
            eventName: event.eventName,
            venueId: event.venueId || null,
          });
        }
      }
    }

    return events;
  }, [data]);

  const availableVenues = useMemo(() => collectAvailableVenues(data), [data]);

  const stats = useMemo(() => {
    if (!summary) return [];

    const nextStats = [
      {
        title: 'Total Revenue',
        value: currencyUnavailable
          ? 'Rates unavailable'
          : formatMoney(summary.totalRevenue, { currency: currencyCode }),
        change: currencyUnavailable ? '—' : totalRevenueChange,
        trend: totalRevenueTrend,
        context: 'Revenue for selected filters',
        featured: true,
      },
      {
        title: 'Invoice Count',
        value: formatNumber(summary.invoiceCount),
        change: '—',
        trend: 'neutral',
        context: 'Total invoices processed',
      },
      {
        title: 'Avg Order Value',
        value: currencyUnavailable
          ? 'Rates unavailable'
          : formatMoney(summary.averageOrderValue, { currency: currencyCode }),
        change: '—',
        trend: 'neutral',
        context: 'Per invoice average',
      },
      {
        title: 'Units Sold',
        value: formatNumber(summary.unitsSold),
        change: '—',
        trend: 'neutral',
        context: 'Total quantity across lines',
      },
    ];

    const summaryComparison = data?.periodComparison?.summaryComparison || null;
    if (summaryComparison) {
      const replaceMetric = (key, statIndex) => {
        const metric = summaryComparison[key];
        if (!metric) return;
        const abs = metric.absoluteChange;
        const pct = metric.percentChange;
        nextStats[statIndex].change = pct == null ? '—' : formatPercent(pct);
        nextStats[statIndex].trend = abs > 0 ? 'up' : abs < 0 ? 'down' : 'neutral';
      };

      replaceMetric('totalRevenue', 0);
      replaceMetric('invoiceCount', 1);
      replaceMetric('averageOrderValue', 2);
      replaceMetric('unitsSold', 3);
    }

    return nextStats;
  }, [summary, currencyCode, currencyUnavailable, totalRevenueChange, totalRevenueTrend, data]);

  const pdfFilterLabels = useMemo(() => {
    const selectedVenue = availableVenues.find(
      (venue) => String(venue.venueId) === String(committedFilters.venue)
    );
    const selectedEvent = availableEvents.find(
      (event) => String(event.eventId) === String(committedFilters.event)
    );

    return {
      timePeriod: TIME_PERIOD_LABELS[committedFilters.timePeriod] || 'All time',
      venue: selectedVenue?.venueName || 'All venues',
      event: selectedEvent?.eventName || 'All events',
      productType: productTypeLabel(committedFilters.productType),
      currency: currencyCode,
    };
  }, [availableEvents, availableVenues, committedFilters, currencyCode]);

  const aovData =
    data?.aovByDate ||
    data?.averageOrderValueByDate ||
    data?.groupedByCurrency?.[sourceCurrency]?.aovByDate ||
    data?.groupedByCurrency?.[sourceCurrency]?.averageOrderValueByDate ||
    [];

  function handleFiltersChange(filters) {
    setCommittedFilters({
      timePeriod: filters.timePeriod || 'all_time',
      start: filters.start || null,
      end: filters.end || null,
      venue: filters.venue || 'all',
      event: filters.event || 'all',
      productType: filters.productType || 'all',
      queryString: filters.queryString || '',
    });
  }

  function handleExportCsv() {
    if (!data || !isFilterPage) return;
    exportPageDashboardData(pageId, data, {
      topEventRows,
      aovData,
      dateLabel: committedFilters.start || new Date().toISOString().slice(0, 10),
      currencyCode,
    });
  }

  async function handleExportPdf() {
    if (!data || !isFilterPage) return;
    try {
      await exportDashboardPdf(
        {
          title: PAGE_REPORT_TITLES[pageId] || 'Venue Intelligence Report',
          reportVariant: PAGE_REPORT_VARIANTS[pageId] || 'overview',
          generatedAt: new Date().toISOString(),
          summary,
          currencyCode,
          filters: committedFilters,
          filterLabels: pdfFilterLabels,
          revenueByDate: data?.revenueByDate || [],
          aovByDate: aovData,
          revenueByProduct: data?.revenueByProduct || [],
          topEventRows,
          topVenues: data?.topVenues || [],
        },
        { filenamePrefix: PAGE_EXPORT_PREFIXES[pageId] || 'venue-intelligence' }
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('PDF export failed', err);
    }
  }

  return {
    committedFilters,
    displayCurrency,
    setDisplayCurrency,
    handleFiltersChange,
    handleExportCsv,
    handleExportPdf,
    data,
    dataSource,
    loading,
    error,
    isFirstLoad,
    isRefreshing,
    panelLoading,
    summary,
    currencyCode,
    stats,
    availableEvents,
    availableVenues,
    topEventRows,
    isTopEventsLoading,
    topEventsError,
    topEventsComparison,
    aovData,
    currencyLoading,
    currencyUnavailable,
    ratesLoading,
    ratesError,
  };
}
